import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { 
  ArrowLeft, 
  CheckCircle2, 
  AlertCircle, 
  Copy, 
  Loader2,
  Smartphone,
  CreditCard,
  Clock,
  Shield
} from 'lucide-react';
import axios from 'axios';
import { Toaster, toast } from '../components/ui/sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentPage() {
  const { submissionId } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [paymentData, setPaymentData] = useState(null);
  const [paymentConfirmed, setPaymentConfirmed] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [confirmCheckbox, setConfirmCheckbox] = useState(false);

  useEffect(() => {
    fetchPaymentDetails();
  }, [submissionId]);

  const fetchPaymentDetails = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/payment/${submissionId}`);
      setPaymentData(response.data);
      
      // Check if already in payment_pending status
      if (response.data.status === 'payment_pending') {
        setPaymentConfirmed(true);
      }
    } catch (err) {
      console.error('Error fetching payment details:', err);
      setError(err.response?.data?.detail || 'Unable to load payment details');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard`);
  };

  const handleConfirmPayment = async () => {
    if (!confirmCheckbox) {
      toast.error('Please confirm that you have made the payment');
      return;
    }

    setConfirming(true);
    try {
      const response = await axios.post(`${API}/payment/${submissionId}/confirm`, {
        submission_id: submissionId,
        confirmed: true
      });
      
      if (response.data.success) {
        setPaymentConfirmed(true);
        toast.success('Payment confirmation submitted!');
      }
    } catch (err) {
      console.error('Error confirming payment:', err);
      toast.error(err.response?.data?.detail || 'Failed to confirm payment');
    } finally {
      setConfirming(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#6ABF4B] animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-black text-white">
        <div className="max-w-2xl mx-auto px-4 py-12">
          <Link to="/" className="inline-flex items-center gap-2 text-[#6ABF4B] hover:underline mb-8">
            <ArrowLeft className="w-4 h-4" /> Back to Home
          </Link>
          
          <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-8 text-center">
            <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-white mb-2">Payment Not Available</h1>
            <p className="text-gray-400">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  // Payment Confirmed Success Screen
  if (paymentConfirmed) {
    return (
      <div className="min-h-screen bg-black text-white">
        <Toaster position="top-center" />
        <div className="max-w-2xl mx-auto px-4 py-12">
          <div className="bg-[#111111] border-2 border-[#6ABF4B] rounded-2xl p-8 md:p-12 text-center">
            <div className="w-20 h-20 bg-[#6ABF4B]/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <Clock className="w-10 h-10 text-[#6ABF4B]" />
            </div>
            <h1 className="text-3xl font-bold text-white mb-4">Payment Confirmation Received!</h1>
            <p className="text-xl text-gray-300 mb-6">Thank you, {paymentData?.name}!</p>
            
            <div className="bg-black rounded-xl p-6 mb-8 text-left">
              <h3 className="text-lg font-semibold text-[#6ABF4B] mb-4">What happens next?</h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 bg-[#6ABF4B] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-white text-sm font-bold">1</span>
                  </span>
                  <span className="text-gray-300">We will verify your payment within <strong className="text-white">24 hours</strong></span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 bg-[#6ABF4B] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-white text-sm font-bold">2</span>
                  </span>
                  <span className="text-gray-300">Once verified, we'll contact you to <strong className="text-white">start your onboarding</strong></span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 bg-[#6ABF4B] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-white text-sm font-bold">3</span>
                  </span>
                  <span className="text-gray-300">You'll receive step-by-step guidance for <strong className="text-white">Uber Eats & DoorDash registration</strong></span>
                </li>
              </ul>
            </div>

            <div className="bg-gray-900/50 rounded-lg p-4 mb-6">
              <p className="text-gray-400 text-sm">
                <strong className="text-white">Payment Reference:</strong> {paymentData?.payment_reference}
              </p>
            </div>

            <Link 
              to="/"
              className="inline-flex items-center gap-2 px-8 py-3 bg-[#6ABF4B] text-white font-semibold rounded-lg hover:bg-[#5aa93f] transition-colors"
            >
              Back to Home
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      <Toaster position="top-center" />
      <div className="max-w-2xl mx-auto px-4 py-12">
        <Link to="/" className="inline-flex items-center gap-2 text-[#6ABF4B] hover:underline mb-8">
          <ArrowLeft className="w-4 h-4" /> Back to Home
        </Link>
        
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">Complete Your Payment</h1>
          <p className="text-gray-400">Hi {paymentData?.name}, please complete your payment using PayID</p>
        </div>

        {/* Package Summary */}
        <div className="bg-[#111111] border border-gray-800 rounded-xl p-6 mb-6">
          <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-[#6ABF4B]" />
            Order Summary
          </h2>
          <div className="flex justify-between items-center py-3 border-b border-gray-800">
            <span className="text-gray-400">Package</span>
            <span className="text-white font-semibold">{paymentData?.package?.name}</span>
          </div>
          <div className="flex justify-between items-center py-3">
            <span className="text-gray-400">Total Amount</span>
            <span className="text-2xl font-bold text-[#6ABF4B]">${paymentData?.package?.price} AUD</span>
          </div>
        </div>

        {/* PayID Instructions */}
        <div className="bg-[#111111] border border-gray-800 rounded-xl p-6 mb-6">
          <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Smartphone className="w-5 h-5 text-[#6ABF4B]" />
            Payment via PayID (Osko)
          </h2>
          
          <div className="space-y-4">
            {/* PayID Email */}
            <div className="bg-black rounded-lg p-4">
              <div className="flex justify-between items-center mb-1">
                <span className="text-gray-400 text-sm">PayID (Email)</span>
                <button 
                  onClick={() => handleCopy(paymentData?.payid_email, 'PayID')}
                  className="text-[#6ABF4B] hover:text-[#5aa93f] flex items-center gap-1 text-sm"
                >
                  <Copy className="w-4 h-4" /> Copy
                </button>
              </div>
              <p className="text-white font-mono text-lg">{paymentData?.payid_email}</p>
            </div>

            {/* Amount */}
            <div className="bg-black rounded-lg p-4">
              <div className="flex justify-between items-center mb-1">
                <span className="text-gray-400 text-sm">Amount</span>
                <button 
                  onClick={() => handleCopy(paymentData?.package?.price.toString(), 'Amount')}
                  className="text-[#6ABF4B] hover:text-[#5aa93f] flex items-center gap-1 text-sm"
                >
                  <Copy className="w-4 h-4" /> Copy
                </button>
              </div>
              <p className="text-white font-mono text-lg">${paymentData?.package?.price} AUD</p>
            </div>

            {/* Payment Reference - Important! */}
            <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
              <div className="flex justify-between items-center mb-1">
                <span className="text-yellow-400 text-sm font-semibold">Payment Reference (REQUIRED)</span>
                <button 
                  onClick={() => handleCopy(paymentData?.payment_reference, 'Reference')}
                  className="text-yellow-400 hover:text-yellow-300 flex items-center gap-1 text-sm"
                >
                  <Copy className="w-4 h-4" /> Copy
                </button>
              </div>
              <p className="text-white font-mono text-lg">{paymentData?.payment_reference}</p>
              <p className="text-yellow-400/80 text-xs mt-2">
                Include this reference in your payment description so we can identify your payment
              </p>
            </div>
          </div>

          {/* Step by step instructions */}
          <div className="mt-6 pt-6 border-t border-gray-800">
            <h3 className="text-sm font-semibold text-gray-300 mb-3">How to pay with PayID:</h3>
            <ol className="space-y-2 text-sm text-gray-400">
              <li className="flex items-start gap-2">
                <span className="text-[#6ABF4B] font-bold">1.</span>
                Open your banking app and select "Pay Anyone" or "Transfer"
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#6ABF4B] font-bold">2.</span>
                Choose PayID as the payment method
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#6ABF4B] font-bold">3.</span>
                Enter the PayID email shown above
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#6ABF4B] font-bold">4.</span>
                Enter the exact amount: ${paymentData?.package?.price} AUD
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#6ABF4B] font-bold">5.</span>
                <strong className="text-yellow-400">Important:</strong> Enter the reference: {paymentData?.payment_reference}
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#6ABF4B] font-bold">6.</span>
                Complete the payment and confirm below
              </li>
            </ol>
          </div>
        </div>

        {/* Confirmation Section */}
        <div className="bg-[#111111] border border-gray-800 rounded-xl p-6">
          <h2 className="text-lg font-semibold text-white mb-4">Confirm Your Payment</h2>
          
          <label className="flex items-start gap-3 cursor-pointer group mb-6">
            <input 
              type="checkbox" 
              checked={confirmCheckbox}
              onChange={(e) => setConfirmCheckbox(e.target.checked)}
              className="mt-1 w-5 h-5 rounded border-gray-600 bg-black text-[#6ABF4B] focus:ring-[#6ABF4B] focus:ring-offset-0 cursor-pointer"
              data-testid="payment-confirm-checkbox"
            />
            <span className="text-gray-300 text-sm group-hover:text-white transition-colors">
              I confirm that I have made a payment of <strong className="text-[#6ABF4B]">${paymentData?.package?.price} AUD</strong> via PayID to <strong className="text-white">{paymentData?.payid_email}</strong> with reference <strong className="text-yellow-400">{paymentData?.payment_reference}</strong>
            </span>
          </label>

          <button
            onClick={handleConfirmPayment}
            disabled={confirming || !confirmCheckbox}
            className="w-full py-4 bg-[#6ABF4B] text-white font-bold text-lg rounded-lg hover:bg-[#5aa93f] transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="payment-confirm-btn"
          >
            {confirming ? (
              <><Loader2 className="w-5 h-5 animate-spin" /> Processing...</>
            ) : (
              <><CheckCircle2 className="w-5 h-5" /> I Have Paid</>
            )}
          </button>

          <p className="text-center text-gray-500 text-sm mt-4 flex items-center justify-center gap-2">
            <Shield className="w-4 h-4" /> We will verify your payment within 24 hours
          </p>
        </div>

        {/* Disclaimer */}
        <div className="mt-6 p-4 bg-gray-900/50 border border-gray-800 rounded-lg">
          <p className="text-gray-500 text-xs text-center">
            <strong>JD NETWORK PTY LTD</strong> (ACN 695 764 071) operating as Just Delivery. 
            Not affiliated with Uber Eats or DoorDash. Platform approval is not guaranteed.
          </p>
        </div>
      </div>
    </div>
  );
}
